import { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import { route, RoutableProps } from 'preact-router';
import { Layout } from '../components/layout';
import { login } from '../services/auth.service';
import { hashPassword } from '../lib/password';
import { ApiError } from '../services/api.service';

type Phase = 'credentials' | 'awaiting-mfa-code';

export const LoginPage: FunctionComponent<RoutableProps> = () => {
  const [phase, setPhase] = useState<Phase>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (busy) return;

    setError(null);
    setBusy(true);

    try {
      const hash = await hashPassword(password);
      const result = await login(email, hash, phase === 'awaiting-mfa-code' ? code : undefined);

      if (result.status === 'mfa-required') {
        setPhase('awaiting-mfa-code');
        setBusy(false);
        return;
      }

      if (result.status === 'permission-denied') {
        setError('Dein Konto hat keine Berechtigung für die TKW Mobile App. Bitte wende dich an deinen Administrator.');
        setPhase('credentials');
        setCode('');
        setBusy(false);
        return;
      }

      route(result.status === 'authenticated' ? '/tkw' : '/mfa/setup', true);
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.status === 401) {
        setError(phase === 'awaiting-mfa-code' ? 'Code ist ungültig.' : 'E-Mail oder Passwort falsch.');
        if (phase === 'awaiting-mfa-code') setCode('');
      } else if (apiError.status === 403) {
        setError('Kein Zugriff für diesen Mandanten.');
      } else {
        setError('Anmeldung fehlgeschlagen. Bitte später erneut versuchen.');
      }
      setBusy(false);
    }
  };

  const useDifferentAccount = () => {
    setPhase('credentials');
    setCode('');
    setPassword('');
    setError(null);
  };

  return (
    <Layout title="TKW Mobile" subtitle="Anmeldung">
      <form onSubmit={submit}>
        {phase === 'credentials' && (
          <>
            <div class="input-group">
              <label for="email">E-Mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                autocomplete="username"
                required
                disabled={busy}
              />
            </div>
            <div class="input-group">
              <label for="password">Passwort</label>
              <input
                id="password"
                type="password"
                value={password}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                autocomplete="current-password"
                required
                disabled={busy}
              />
            </div>
          </>
        )}

        {phase === 'awaiting-mfa-code' && (
          <div class="input-group">
            <label for="code">Code aus deiner Authenticator-App</label>
            <input
              id="code"
              type="text"
              class="input code"
              value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autocomplete="one-time-code"
              maxLength={6}
              required
              disabled={busy}
            />
            <div class="hint">6-stelliger Code, alle 30 Sekunden neu</div>
          </div>
        )}

        {error && <div class="error">{error}</div>}

        <button type="submit" class="btn" disabled={busy}>
          {busy ? <span class="spinner" /> : phase === 'awaiting-mfa-code' ? 'Bestätigen' : 'Anmelden'}
        </button>

        {phase === 'awaiting-mfa-code' && (
          <button type="button" class="btn link" onClick={useDifferentAccount}>
            Anderes Konto verwenden
          </button>
        )}

        {phase === 'credentials' && (
          <div class="center mt-md">
            <a class="btn link" href="/forgot-password" onClick={(e) => { e.preventDefault(); route('/forgot-password'); }}>
              Passwort vergessen?
            </a>
          </div>
        )}
      </form>
    </Layout>
  );
};
